import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tab, Tabs } from '@repo/design-system/components/ds/tabs/tabs';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import { useAppDispatch } from '@repo/store/maps/hooks';
import { setSelectedLabelId } from '@repo/store/maps/slices/mapData';
import type { MapDataLabel } from '@repo/types/maps/map-types';
import { ColorPicker } from '@repo/ui/components/color-picker/color-picker';
import { useEffect, useRef, useState } from 'react';
import { CategoryColorPalette } from '../creator-tools/components/category-color-palette';
import { useLabelOperations } from '../game-map/hooks/useLabelOperations';
import { useRegionLabelRenderer } from '../game-map/hooks/useRegionLabelRenderer';
const LABEL_SIZES = {
  large: 24,
  medium: 20,
  small: 16
} as const;
export const LabelInfoEditing = ({
  label
}: {
  label: MapDataLabel;
}) => {
  const dispatch = useAppDispatch();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    startMovingLabel,
    updateLabelProperties,
    deleteLabelById,
    uploadIconForLabel,
    isUpdating,
    isDeleting,
    isUploadingIcon
  } = useLabelOperations();
  const {
    refreshLabelIcon
  } = useRegionLabelRenderer();
  const [title, setTitle] = useState(label.properties.title || '');
  const [color, setColor] = useState(label.properties.color || '#ffffff');
  const [labelSize, setLabelSize] = useState<keyof typeof LABEL_SIZES>(() => {
    const size = label.properties.fontSize || 18;
    if (size >= LABEL_SIZES.large) return 'large';
    if (size >= LABEL_SIZES.medium) return 'medium';
    return 'small';
  });
  const [minZoom, setMinZoom] = useState<string>(label.properties.minZoom?.toString() || '');
  const [maxZoom, setMaxZoom] = useState<string>(label.properties.maxZoom?.toString() || '');
  useEffect(() => {
    setTitle(label.properties.title || '');
    setColor(label.properties.color || '#ffffff');
    const size_0 = label.properties.fontSize || 18;
    setLabelSize(size_0 >= LABEL_SIZES.large ? 'large' : size_0 >= LABEL_SIZES.medium ? 'medium' : 'small');
    setMinZoom(label.properties.minZoom?.toString() || '');
    setMaxZoom(label.properties.maxZoom?.toString() || '');
  }, [label]);
  const handleSave = async () => {
    const fontSize = LABEL_SIZES[labelSize];
    await updateLabelProperties(label.properties.externalId, {
      title,
      color,
      fontSize,
      minZoom: minZoom ? parseFloat(minZoom) : undefined,
      maxZoom: maxZoom ? parseFloat(maxZoom) : undefined,
      iconId: label.properties.iconId
    }, label.geometry);
  };
  const handleDelete = async () => {
    if (confirm(`Delete label "${label.properties.title || 'Unnamed'}"?`)) {
      await deleteLabelById(label.properties.externalId);
      dispatch(setSelectedLabelId(null));
    }
  };
  const handleMove = () => {
    startMovingLabel(label);
  };
  const handleIconFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        try {
          const iconId = await uploadIconForLabel(label.properties.externalId, file);
          if (iconId) {
            const fontSize_0 = LABEL_SIZES[labelSize];
            await updateLabelProperties(label.properties.externalId, {
              title,
              color,
              fontSize: fontSize_0,
              minZoom: minZoom ? parseFloat(minZoom) : undefined,
              maxZoom: maxZoom ? parseFloat(maxZoom) : undefined,
              iconId
            }, label.geometry);
            await refreshLabelIcon(label.properties.externalId, iconId);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }
        } catch (error) {
          console.error('Icon upload error:', error);
        }
      } else {
        toast({
          title: 'Invalid file',
          description: 'Please select an image file',
          variant: 'error'
        });
      }
    }
  };
  const handleClose = () => {
    dispatch(setSelectedLabelId(null));
  };
  return <>
            <FlexRow align="center" gap="8" width="full" className={css.raw({
      padding: '4',
      paddingBottom: '12'
    })}>
                <Type variant="header-s" color="white">
                    Edit Label
                </Type>
                <DropdownMenu trigger={<IconButton color="secondary" size="sm" aria-label="More options" className={css({
        marginLeft: 'auto'
      })}>
                            <Icon icon="more_vert" decorative />
                        </IconButton>}>
                    <DropdownMenuItem value="move" leadingIcon="open_with" onSelect={handleMove}>
                        Move Label
                    </DropdownMenuItem>
                    <DropdownMenuItem value="delete" leadingIcon="delete" onSelect={handleDelete} disabled={isDeleting} destructive>
                        Delete Label
                    </DropdownMenuItem>
                </DropdownMenu>
                <IconButton onClick={handleClose} color="secondary" size="sm" aria-label="Close">
                    <Icon icon="close" decorative />
                </IconButton>
            </FlexRow>

            <ScrollableContent maxHeight={600} className={css({
      overflow: 'hidden',
      display: 'flex',
      flex: '1'
    })}>
                <FlexColumn gap="12" width="full" className={css.raw({
        padding: '8'
      })}>
                    <TextInput ref={titleInputRef} value={title} onChange={e_0 => setTitle(e_0.target.value)} placeholder="Label Title" label="Title" size="sm" fullWidth />

                    <FlexColumn gap="8">
                        <ColorPicker label="Color" value={color} onChange={setColor} size="sm" />
                        <CategoryColorPalette selectedColor={color} onColorSelect={setColor} label="Choose custom color, or one from palette" />
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Size
                        </Type>
                        <Tabs role="radiogroup" fullWidth aria-label="Size" value={labelSize} onValueChange={value => setLabelSize(value as keyof typeof LABEL_SIZES)}>
                            <Tab value="large">Large</Tab>
                            <Tab value="medium">Medium</Tab>
                            <Tab value="small">Small</Tab>
                        </Tabs>
                    </FlexColumn>

                    <FlexColumn gap="8">
                        <Type variant="body-s" color="text.secondary">
                            Icon (Optional)
                        </Type>
                        {(label.properties.iconPath || label.properties.iconId) && <FlexRow gap="8" align="center">
                                <img src={label.properties.iconId ? `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_label_icon/${label.properties.iconId}/50.webp` : label.properties.iconPath?.startsWith('http') ? label.properties.iconPath : `${process.env.NEXT_PUBLIC_CDN_URL}${label.properties.iconPath}`} alt="Label icon" style={{
              width: '32px',
              height: '32px'
            }} />
                                <Type variant="body-s">Current icon</Type>
                            </FlexRow>}
                        <Button onClick={() => fileInputRef.current?.click()} variant="filled" color="secondary" size="sm" fullWidth disabled={isUploadingIcon}>
                            <Icon icon="upload" decorative />
                            {isUploadingIcon ? 'Uploading...' : 'Choose Icon'}
                        </Button>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleIconFileChange} style={{
            display: 'none'
          }} />
                    </FlexColumn>

                    <FlexRow gap="8" width="full">
                        <TextInput value={minZoom} onChange={e_1 => setMinZoom(e_1.target.value)} placeholder="Min Zoom" label="Min Zoom" type="number" size="sm" fullWidth />
                        <TextInput value={maxZoom} onChange={e_2 => setMaxZoom(e_2.target.value)} placeholder="Max Zoom" label="Max Zoom" type="number" size="sm" fullWidth />
                    </FlexRow>
                </FlexColumn>
            </ScrollableContent>

            <FlexColumn gap="8" justify="end" width="full">
                <Button onClick={handleSave} color="success" size="sm" fullWidth disabled={isUpdating}>
                    {isUpdating ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="ghost" onClick={handleClose} color="primary" size="sm" fullWidth>
                    Close
                </Button>
            </FlexColumn>
        </>;
};